import React, { useState, useRef, useEffect, useCallback } from 'react'
import { makeStyles } from '@mui/styles'
import TextInput from '@/modules/base/components/inputs/text-input/text-input'
import clsx from 'clsx'
import ThemeInterface from '@/theme-interface'

export const STATUS = {
  default: 'default',
  valid: 'valid',
  invalid: 'invalid',
}

const DIGIT_NAMES = ['firstDigit', 'secondDigit', 'thirdDigit', 'fourthDigit'] as const
type DigitKey = (typeof DIGIT_NAMES)[number]

const emptyDigits = (): Record<DigitKey, string> => ({
  firstDigit: '',
  secondDigit: '',
  thirdDigit: '',
  fourthDigit: '',
})

interface IDigitForm {
  status: string
  onConfirm: (code: string) => void
  setIsDirty?: (isDirty: boolean) => void
  dataQsId: string
  slotClassName?: string
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  form: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',

    '&.invalid $digitSlot input': {
      borderColor: theme.red,
      color: theme.red,
    },
  },
  digitSlot: {
    maxWidth: 80,
    width: 80,
    marginRight: 20,
    '&:last-of-type': {
      margin: 0,
    },
    '& input': {
      textAlign: 'center',
      width: '100%',
      height: 80,
      fontSize: 24,
      padding: 0,
      color: theme.primaryDark,
      borderColor: '#b3b9c6',
      backgroundColor: theme.white,
      boxShadow: '0 3px 5px 0 rgba(206, 216, 240, 0.8)',
      '&::placeholder': {
        color: theme.auth.code.form.placeholder.color,
      },
    },
  },
}))

const takeFirstDigit = (raw: string) => raw.replace(/\D/g, '').slice(0, 1)

const DigitForm = (props: IDigitForm) => {
  const { status = STATUS.default, setIsDirty, onConfirm, dataQsId, slotClassName } = props
  const classes = useStyles()
  const [digits, setDigits] = useState<Record<DigitKey, string>>(emptyDigits)
  const [formStatus, setFormStatus] = useState(status)

  const firstDigitRef = useRef<HTMLInputElement | null>(null)
  const secondDigitRef = useRef<HTMLInputElement | null>(null)
  const thirdDigitRef = useRef<HTMLInputElement | null>(null)
  const fourthDigitRef = useRef<HTMLInputElement | null>(null)
  const inputRefs = [firstDigitRef, secondDigitRef, thirdDigitRef, fourthDigitRef]

  useEffect(() => {
    const isDirty = DIGIT_NAMES.some(k => digits[k] !== '')
    if (isDirty) setIsDirty?.(true)
  }, [digits, setIsDirty])

  const focusDigit = useCallback((idx: number) => {
    if (idx >= 0 && idx < DIGIT_NAMES.length) {
      inputRefs[idx].current?.focus()
    }
  }, [])

  // a whole code at once (paste, the iOS code suggestion from the mail) is spread over the four fields
  const fillCode = useCallback((raw: string) => {
    const chars = raw.replace(/\D/g, '').slice(0, DIGIT_NAMES.length)
    if (!chars.length) return

    const split = chars.split('')
    setDigits({
      firstDigit: split[0] ?? '',
      secondDigit: split[1] ?? '',
      thirdDigit: split[2] ?? '',
      fourthDigit: split[3] ?? '',
    })
    focusDigit(Math.min(chars.length, DIGIT_NAMES.length - 1))
  }, [focusDigit])

  const handleChange = useCallback((value: string, name: string) => {
    // autocomplete='one-time-code' inserts the full code into the focused field, whichever it is
    if (value.replace(/\D/g, '').length > 1) {
      fillCode(value)
      return
    }
    const key = name as DigitKey
    const digit = takeFirstDigit(value)
    setDigits(prev => ({ ...prev, [key]: digit }))
    // Fallback for environments where keydown.key is unreliable (mobile/IME)
    if (digit) focusDigit(DIGIT_NAMES.indexOf(key) + 1)
  }, [focusDigit, fillCode])

  const handlePaste = useCallback((raw: string, _name: string, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    fillCode(raw)
  }, [fillCode])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>, name: string) => {
    const key = name as DigitKey
    const idx = DIGIT_NAMES.indexOf(key)
    const { key: pressed } = event

    if (event.ctrlKey || event.metaKey || event.altKey) return

    if (pressed === 'Backspace' || pressed === 'Delete') {
      event.preventDefault()
      setDigits(prev => ({ ...prev, [key]: '' }))
      if (pressed === 'Backspace') focusDigit(idx - 1)
      return
    }

    if (['Tab', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(pressed)) return

    if (pressed.length === 1 && pressed >= '0' && pressed <= '9') {
      event.preventDefault()
      setDigits(prev => ({ ...prev, [key]: pressed }))
      focusDigit(idx + 1)
      return
    }

    if (pressed.length === 1) event.preventDefault()
  }, [focusDigit])

  const handleConfirmClick = (event: React.FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const code = DIGIT_NAMES.map(k => digits[k]).join('')
    onConfirm?.(code)
  }

  useEffect(() => {
    setFormStatus(status)
  }, [status])

  useEffect(() => {
    const code = DIGIT_NAMES.map(k => digits[k]).join('')
    if (code.length !== DIGIT_NAMES.length) return
    onConfirm?.(code)
  }, [digits, onConfirm])

  return (
    <form className={clsx(classes.form, formStatus === STATUS.invalid && 'invalid')} id='code' onSubmit={handleConfirmClick}>
      {DIGIT_NAMES.map((name, index) => (
        <TextInput
          key={name}
          useFormik={false}
          selectOnFocus
          name={name}
          ref={inputRefs[index]}
          // numeric keypad with the code from the mail above it (iOS) - no maxLength, it would cut the
          // suggested code down to its first digit
          inputMode='numeric'
          placeholder=''
          value={digits[name]}
          customClass={clsx(classes.digitSlot, slotClassName)}
          autoComplete='one-time-code'
          autoFocus={index === 0}
          onChange={handleChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          dataQsId={`${dataQsId}-${index + 1}`}
          enableAiFeatures={false}
        />
      ))}
    </form>
  )
}

export default DigitForm
